import { useTenantRoute } from '@/account/hook/useTenantRoute';
import { type IGoogleKeyResponse } from '@/api/registration/gtm';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { tenantGTMQueryKey } from '@/lib/hook/useGTM';
import { UiBox } from '@/lib/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FC } from 'react';
import CookieConsent, { Cookies } from 'react-cookie-consent';

interface CookieBannerProps {
  data: ApiResponseSingle<IGoogleKeyResponse> | undefined
  isGivenCookieConsent: boolean
  sendGTMEvent: (event: string, data: Record<string, string>) => void
}

const CookieBanner: FC<CookieBannerProps> = ({ sendGTMEvent, data }) => {
  const queryClient = useQueryClient();
  const { tenant, tenantCode } = useTenantRoute();
  const isExistingCookieConsent = Boolean(Cookies.get(`CookieConsent-${tenantCode}`));
  const [isOpenOverlay, setOpenOverlay] = useState(!isExistingCookieConsent);
  return data?.item?.googleTagManagerKey && <>
    {isOpenOverlay && <UiBox
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.500"
      zIndex={9998}
    />}
    <CookieConsent
      location="bottom"
      buttonText="Accept"
      declineButtonText="Decline"
      enableDeclineButton
      cookieName={`CookieConsent-${tenantCode}`}
      onAccept={() => {
        Cookies.set(`CookieConsent-${tenantCode}`, 'true');
        sendGTMEvent('cookieAccepted', { action: 'accept' });
        setOpenOverlay(false);
        void queryClient.invalidateQueries({ queryKey: [tenantGTMQueryKey, { tenantId: tenant?.id }] });
      }}
      onDecline={() => {
        Cookies.set(`CookieConsent-${tenantCode}`, 'false');
        setOpenOverlay(false);
        sendGTMEvent('cookieDeclined', { action: 'decline' });
      }}
      style={{
        width: 'fit-content',
        background: 'white',
        boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
        padding: '24px',
        maxWidth: '480px',
        margin: '20px',
        marginBottom: '55px',
        borderRadius: '12px',
        left: '0',
        right: 'auto',
        zIndex: 9999
      }}
      contentStyle={{
        margin: 0,
        padding: 0,
        color: '#1a1a1a',
        textAlign: 'left'
      }}
      buttonStyle={{
        background: '#4169E1',
        color: 'white',
        padding: '12px 24px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '16px',
        fontWeight: '500',
        flex: 1,
        margin: 0,
        marginTop: '16px'
      }}
      declineButtonStyle={{
        background: 'white',
        border: '1.5px solid #4169E1',
        color: '#4169E1',
        padding: '12px 24px',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '500',
        flex: 1,
        margin: 0,
        marginRight: '16px',
      }}
    >
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#4169E1">
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8zm-1-5h2v2h-2v-2zm0-8h2v6h-2V7z" />
        </svg>
        <span style={{
          color: '#4169E1',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          {data?.item.gdprCookieBannerHeader ?? 'Do you agree to let us use cookies?'}
        </span>
      </div>
      <div>
        {data?.item.gdprCookieBannerText ?? 'We use cookies to enhance your experience and ensure the proper functionality of the site. Cookies are necessary for certain features of our website. By continuing to browse, you consent to the use of cookies. We do not share your information for advertising purposes.'}
      </div>
    </CookieConsent></>;
};

export default CookieBanner;
